/**
 * RequirePlan — wraps a route and checks if the user's plan includes it.
 * Usage: <RequirePlan plans={["plus","premium"]}><TimeTracker /></RequirePlan>
 */
import { type ReactElement } from "react"
import { Link } from "react-router-dom"
import { Lock } from "lucide-react"
import { useAuth, type Plan } from "../context/AuthContext"
import { useLang } from "../context/LangContext"

interface Props {
  plans: Array<Plan>
  children: ReactElement
}

const T = {
  en: {
    title: "Upgrade required",
    sub: "This tool is available on the Plus plan and above.",
    cta: "See plans",
    back: "Go back",
  },
  es: {
    title: "Plan superior requerido",
    sub: "Esta herramienta está disponible en el plan Plus y superior.",
    cta: "Ver planes",
    back: "Volver",
  },
}

export default function RequirePlan({ plans, children }: Props) {
  const { plan, loading } = useAuth()
  const { lang } = useLang()
  const t = T[lang]

  if (loading) return null

  const currentPlan = (plan || "free") as string
  if (plans.includes(currentPlan as any)) {
    return children
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
      <div className="w-14 h-14 rounded-2xl bg-[#F8FAFF] dark:bg-neutral-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center mb-5">
        <Lock className="h-6 w-6 text-[#2563EB]" />
      </div>
      <h2 className="text-2xl font-semibold text-black dark:text-white mb-2">{t.title}</h2>
      <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-sm">{t.sub}</p>
      <div className="flex gap-3">
        <Link
          to="/pricing"
          className="px-6 py-2.5 rounded-xl bg-[#2563EB] text-white text-sm font-medium hover:bg-[#1d4ed8] transition"
        >
          {t.cta}
        </Link>
        <button
          onClick={() => window.history.back()}
          className="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-neutral-800 transition"
        >
          {t.back}
        </button>
      </div>
    </div>
  )
}
